import { useState, useMemo } from "react";
import { useFetch } from "../../../../../hooks/api";
import { useLocation, createFileRoute } from "@tanstack/react-router";
import { connect } from "react-redux";
import moment from "moment";
import { Row, Col, Badge } from "react-bootstrap";
import MetricsCard from "../../../../../components/custom/merchandise/MetricsCard";
import DetailedMetric from "../../../../../components/core/blocks/DetailedMetric";
import { LAUNCH_STATES } from "../../../../../components/custom/merchandise/launch/constants";
import {
  formatCurrency,
  formatNumber,
  formatPercent,
  parseFormattedCurrency,
  parseFormattedNumber,
  parseFormattedPercentage,
} from "../../../../../utils/formatNumber";
import { SALE_STATES } from "../../../../../components/core/blocks/AdPlan/constants";
import PlanStatus from "../../../../../components/custom/merchandise/PlanStatus";
import LaunchPlanningDialogue from "../../../../../components/custom/merchandise/launch/LaunchPlanningDialogue";
import LaunchEditModal from "../../../../../components/custom/merchandise/launch/LaunchEditModal";
import api from "../../../../../utils/api";
import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useGetV4ProductReportTotals } from "../../../../../modules/perf_reports/api/product/v4ProductReportTotals";
import { useGetV4ProductReportByDate } from "../../../../../modules/perf_reports/api/product/v4ProductReportByDate";
import { useGetV4ProductReportByProduct } from "../../../../../modules/perf_reports/api/product/v4ProductReportByProduct";
import BreadcrumbDates from "../../../../../components/core/blocks/BreadcrumbDates";
import { trellisPalette } from "../../../../../components/custom/analytics/palettes";

import ConfirmationModal from "../../../../../components/core/blocks/ConfirmationModal";
import withMediaQuery from "../../../../../hocs/withMediaQuery";
import { Accordion } from "../../../../../components/core/basic/Accordion";
import Loading from "../../../../../components/core/blocks/Loading";
import { DatesProvider } from "../../../../../dates/DatesContext";
import { useDates } from "../../../../../dates/DatesContext";
import { useDateFormatter } from "../../../../../i18n/useDateFormatter";
import { useDebounce } from "@reactuses/core";
import getMetricSpec from "../../../../../modules/merchandising/utils/metrics";
import { filterRanges, generateFilterMarkup, generateRanges } from "../../../../../utils/rangeDropdownFilter";
import TrComponent from "../../../../../components/custom/merchandise/TrComponent";
import ExpanderCell from "../../../../../components/custom/merchandise/cells/ExpanderCell";
import { DownloadV4Report } from "../../../../../modules/perf_reports/DownloadReport";
import { useV4ProductReportExport } from "../../../../../modules/perf_reports/api/product/v4ProductReportExport";

import useColumnSelector from "../../../../../hooks/useColumnSelector";
import ReactTable from "react-table-6";
import ReactTablePagination from "../../../../../components/custom/growth/ProductsCVTablePagination";
import ProductCell from "../../../../../components/custom/merchandise/cells/ProductCell";
import { snakeCaseToSentenceCase } from "../../../../../utils/formatText";
import CogsEditModal from "../../../../../modules/merchandising/components/CogsEditModal";
import LaunchRetrospectiveDialogue from "../../../../../components/custom/merchandise/launch/LaunchRetrospectiveDialogue";
import ProductCard from "../../../../../components/custom/merchandise/ProductCard";
import { CustomTriggerTooltip, HoverInfo } from "../../../../../components/core/basic/Tooltip";
import { useSimpleCan } from "../../../../../config/casl/casl";
import { useTranslation } from "react-i18next";

// Maps backend stock_status enum values to the user-facing English keys in merchandising.json
const STOCK_STATUS_TO_KEY = {
  IN_STOCK: "In Stock",
  LOW_STOCK: "Low Stock",
  IN_STOCK_SCARCE: "Low Stock",
  OUT_OF_STOCK: "Out of Stock",
  OVERSTOCK: "Overstock",
};

// Factored out from buildMetric to separate LaunchDetails-specific override logic
const buildLaunchMetric = (marketPlace, data, id, compareId, options = {}) => {
  const {
    // Override options that were previously passed to buildMetric
    override,
    overrideValue,
    overrideLabel,
    overrideTooltip,
    overrideTarget,
    overrideTargetValue,
    overrideTargetLabel,
    overrideTargetFormatter,
    overrideTargetLower,
  } = options;

  const metric = getMetricSpec(id);

  // Apply LaunchDetails-specific override logic
  if (override) {
    metric.actual = overrideValue ?? metric.actual;
    metric.name = overrideLabel ?? metric.name;
    metric.toolTip = overrideTooltip ?? metric.toolTip;
  }

  if (overrideTarget) {
    metric.targetLabel = overrideTargetLabel;
    metric.target = overrideTargetValue;
    metric.targetFormatter = overrideTargetFormatter;
    metric.lower = overrideTargetLower;
  }

  return metric;
};

const col = (header, type, key, options) => ({
  header,
  type,
  key,
  options,
});

const makeColumn =
  (
    data,
    marketPlace,
    mobileStyle,
    date,
    setIsExpanded,
    launchData,
    additionalBadges,
    selectedProduct,
    setSelectedProduct,
    isReadOnly = false,
    t,
  ) =>
  (header, key, type, options) => {
    const { hideFilter } = options ?? {};

    const defaultAccessor = (p) => p[key];
    const accessor = options?.accessor ?? defaultAccessor;
    const defaultFormat = ({ value }) => value;

    const fmt =
      options?.formatter ??
      {
        expander: ({ row, value }) => (
          <ExpanderCell row={row} valueAccessor={"product_id"} isExpanded={value} setIsExpanded={setIsExpanded} />
        ),
        currency: ({ value }) =>
          formatCurrency(value, {
            marketPlace: marketPlace.marketPlace,
            decimalPlaces: options?.decimalPlaces ?? 0,
          }),
        number: ({ value }) => formatNumber(value, { decimalPlaces: options?.decimalPlaces ?? 1 }),
        percent: ({ value }) =>
          formatPercent(value, {
            decimalPlaces: options?.decimalPlaces ?? 1,
          }),
        cogs: ({ row }) => {
          const { setProductEditModalDetails, setShowProductEditModal } = options.cellFunctions;
          return (
            <div
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (isReadOnly) {
                  return;
                }
                setProductEditModalDetails(row._original);
                setShowProductEditModal(true);
              }}
            >
              {formatCurrency(row._original.landed_cost + row._original.shipping_cost, {
                marketPlace: marketPlace.marketPlace,
              })}
              {!isReadOnly && <i className="fa fa-pencil ps-3 text-muted"></i>}
            </div>
          );
        },
        stock: ({ row }) => {
          const stockStatus = row?._original?.stock_status ? row?._original?.stock_status : "IN_STOCK";
          const stockKey = STOCK_STATUS_TO_KEY[stockStatus];
          return <>{stockKey ? t(($) => $.merchandising[stockKey]) : snakeCaseToSentenceCase(stockStatus)}</>;
        },
        product: ({ row, value }) => {
          if (!value) {
            return row?._original?.sku;
          }
          return (
            <ProductCell
              value={value}
              row={row}
              product={{ ...row._original, id: row._original?.product_id }}
              additionalBadges={
                row._original?.vine_program_enrolled ?
                  <Badge className="badge-secondary" style={{ marginLeft: "0.5rem", backgroundColor: "#c43066" }} pill>
                    {t(($) => $.merchandising["Vine"])}
                  </Badge>
                : null
              }
              salesStateArray={[
                SALE_STATES.BEST_SELLER,
                SALE_STATES.NORMAL,
                SALE_STATES.CLEARANCE,
                SALE_STATES.NEW_PRODUCT,
              ]}
              showSalesLabelBadge={
                row?._original?.sales_state?.toUpperCase() !== SALE_STATES.NEW_PRODUCT.value &&
                launchData?.state?.toUpperCase() === LAUNCH_STATES.LAUNCHED.value
              }
              selectedProduct={launchData?.products?.length > 1 ? selectedProduct : null}
              setSelectedProduct={launchData?.products?.length > 1 ? setSelectedProduct : null}
              wrapProductTitle={true}
            />
          );
        },
        percentDiff: ({ value }) => (
          <span
            style={{
              color: value > 0 ? trellisPalette[12] : trellisPalette[17],
            }}
          >
            {formatPercent(value, {
              decimalPlaces: 0,
              addPlus: true,
            })}
          </span>
        ),
      }?.[type] ??
      defaultFormat;
    const parser =
      {
        currency: parseFormattedCurrency,
        number: parseFormattedNumber,
        percent: parseFormattedPercentage,
      }?.[type] ?? defaultFormat;

    let width;
    if (mobileStyle) {
      width = options?.mobileWidth;
    } else {
      width = options?.width;
    }

    // Handle percent filter formatting
    if (type === "percent") {
      data = data.map((row) => {
        Object.keys(row).forEach((propertyKey) => {
          if (propertyKey === key) {
            row["formatted_" + propertyKey] = row[propertyKey] * 100;
          }
        });

        return row;
      });
    }

    return {
      Header: header,
      id: key,
      accessor,
      checked: options?.checked ?? true,
      maxWidth: options?.maxWidth,
      minWidth: options?.minWidth,
      isStatic: options?.isStatic,
      width,
      height: 30,
      className: options?.className ?? "text-end",
      headerClassName: options?.className ?? "text-end",
      Cell: (props) => {
        if (options?.showDot ?? true) {
          const percentKey = `${key}_percent`;
          const percent = props.original?.[percentKey] ?? null;
          const isNegative = options?.isNegative ?? false;
          let cellClasses = options?.cellClasses ?? "";
          const compareValue = options?.compareValue;
          const staticCompareValue = options?.staticCompareValue;
          let color = "#666";

          const launchDuration = moment(launchData.end_date).diff(moment(launchData.start_date), "days");
          const daysIntoLaunch = moment().diff(moment(launchData.start_date), "days");
          const progressPercent = daysIntoLaunch / launchDuration;
          const currentTarget =
            staticCompareValue ? compareValue : (compareValue * progressPercent) / launchData?.products?.length;

          if (compareValue && props.value > currentTarget) {
            color = isNegative ? trellisPalette[17] : trellisPalette[12];
          }
          if (compareValue && props.value < currentTarget) {
            color = isNegative ? trellisPalette[12] : trellisPalette[17];
          }
          const totalRow = props.original.sku === "Launch Goal" || props.original.sku === "Launch Total";

          if (totalRow) {
            cellClasses = "py-4";
          }
          if (totalRow && percentKey === "isExpanded_percent") {
            return null;
          }
          return (
            <div className={`${cellClasses} d-flex flex-column align-items-end`}>
              {fmt({ ...props, date })}
              <span
                style={{
                  color,
                  fontSize: "1.2rem",
                  fontWeight: "bold",
                }}
              >
                {percent === null ? null : (
                  formatPercent(percent, {
                    decimalPlaces: 0,
                    addPlus: true,
                  })
                )}
                {/* {compareValue && props.original.sku !== "Launch Goal" ? (
                  <> ({formatPercent(props.value / compareValue)})</>
                ) : null} */}
              </span>
              {compareValue && props.original.sku !== "Launch Goal" && (
                <CustomTriggerTooltip
                  toolTip={
                    staticCompareValue ?
                      t(($) => $.merchandising["Showing listing performance trend vs. launch goals"])
                    : t(
                        ($) =>
                          $.merchandising[
                            "Showing listing performance trend vs. launch goals based on how far into the launch divided by the number of launch products."
                          ],
                      )
                  }
                  position="top"
                >
                  <span
                    style={{
                      color,
                      fontSize: "1.2rem",
                      fontWeight: "bold",
                      display: "block",
                    }}
                    role={"button"}
                  >
                    {compareValue ?
                      staticCompareValue ?
                        formatPercent(props.value / compareValue, {
                          decimalPlaces: 0,
                          addPlus: true,
                        })
                      : totalRow ?
                        formatPercent(props.value / compareValue, {
                          decimalPlaces: 0,
                          addPlus: true,
                        })
                      : formatPercent((props.value / compareValue) * launchData?.products?.length, {
                          decimalPlaces: 0,
                          addPlus: true,
                        })

                    : ""}
                  </span>
                </CustomTriggerTooltip>
              )}
            </div>
          );
        }
        return fmt({ ...props, date });
      },

      style: {},
      filterMethod: (filter, row) => {
        const formattedPercentAccessor = (p) => p[`formatted_${key}`];
        const value = type === "percent" ? formattedPercentAccessor(row._original) : accessor(row._original);
        return filterRanges(filter, value, parser, marketPlace.marketPlace);
      },
      Filter: ({ filter, onChange }) => {
        if (hideFilter) {
          return null;
        }

        return generateFilterMarkup(filter, onChange, key, "range", type, {
          ranges: generateRanges(
            data ?? [],
            5,
            type === "percent" ? `formatted_${key}` : key,
            "number",
            marketPlace.marketPlace,
          ),
          marketPlace: marketPlace.marketPlace,
        });
      },
    };
  };

const Table = ({
  stats,
  marketPlace,
  mobileStyle,
  columnSpecs = [],
  date,
  user,
  isLoading = false,
  additionalBadges,
  launchData,
  selectedProduct,
  setSelectedProduct,
  onUpdateStatus,
  preparingState,
  hidePagination,
  noHeader,
  isReadOnly = false,
}) => {
  const [searchFilter, setSearchFilter] = useState("");
  const [clearFilters, setClearFilters] = useState(false);
  const [isExpanded, setIsExpanded] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [colToggle] = useState();
  const { t } = useTranslation(["merchandising"]);

  const debouncedSearchFilter = useDebounce(searchFilter, 275);

  const data = useMemo(
    () => {
      return (
        stats
          ?.map((p) => (p.product_id === isExpanded ? { ...p, isExpanded } : p))
          ?.filter((p) => {
            if (debouncedSearchFilter) {
              const searchable = `${p.category_name} ${p.product_title} ${p.sku} ${p.asin} `;
              return searchable.toLowerCase()?.indexOf(debouncedSearchFilter.toLowerCase()) > -1;
            }
            return true;
          }) ?? []
      );
    },
    // Below linting disabled to take clearFilters in to recalcalculate data on filter reset
    // Without it, filter specs from react-table must be stored at this level and manually cleared on each reset

    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [stats, debouncedSearchFilter, clearFilters, isExpanded],
  );

  const createColumn = makeColumn(
    data,
    marketPlace,
    mobileStyle,
    date,
    setIsExpanded,
    launchData,
    additionalBadges,
    selectedProduct,
    setSelectedProduct,
    isReadOnly,
    t,
  );

  const [columns, columnSelectorProps] = useColumnSelector(
    () => columnSpecs.map((c) => createColumn(c.header, c.key, c.type, c.options)),
    [columnSpecs, stats, mobileStyle, colToggle],
    "advertisingDashboard",
  );
  const columnOrder = columns.map((col) => col.id);

  if (isLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }
  const HiddenTheadComponent = () => null;

  return (
    <ReactTable
      showPaginationBottom={false}
      showPaginationTop={!hidePagination}
      PaginationComponent={ReactTablePagination}
      columns={columns}
      data={data}
      defaultPageSize={10}
      minRows={0}
      className="-highlight"
      filterable={showFilters}
      sortable={!hidePagination}
      state={columnOrder}
      TrComponent={TrComponent}
      TheadComponent={noHeader && HiddenTheadComponent}
      defaultSorted={[{ id: "ad_spend", desc: false }]}
      getTrProps={(table, row) => {
        const style = {
          border: "none",
          padding: "1rem 0",
          maxWidth: "100%",
        };
        const expandedChildren = (
          <ProductCard
            marketPlace={{ marketPlace: marketPlace }}
            productId={row?.original?.product_id}
            user={user}
            initialTab={
              launchData?.state?.toLowerCase() === LAUNCH_STATES.LAUNCHED.value.toLowerCase() ? "launch" : "overview"
            }
            additionalTabs={
              preparingState || launchData?.state?.toUpperCase() === LAUNCH_STATES.INTRODUCED.value ?
                []
              : [
                  {
                    title: t(($) => $.merchandising["Launch"]),
                    content: (
                      <LaunchRetrospectiveDialogue
                        productStats={row?.original ?? {}}
                        productDetails={row?.original ?? {}}
                        launchData={launchData}
                        productId={row?.original?.product_id}
                        productState={row?.original?.sales_state}
                        onUpdateStatus={onUpdateStatus}
                        marketPlace={marketPlace}
                      />
                    ),
                  },
                ]
            }
          />
        );
        return { row, style, expandedChildren };
      }}
      getTheadFilterProps={(props) => {
        if (clearFilters) {
          props.filtered.forEach((filter) => {
            filter.value = "all";
          });
          setClearFilters(false);
        }
        return { style: { display: "flex" } };
      }}
      getPaginationProps={() => ({
        updateSearch: setSearchFilter,
        clearFilters: () => {
          setSearchFilter("");
          setClearFilters(true);
        },
        hideFilters: false,
        showFilters: showFilters,
        setShowFilters: setShowFilters,
        clearSearchTermFilters: () => setSearchFilter(""),
        searchTerm: searchFilter,
        table: "plan-products",
        style: { marginBottom: "2rem" },
        carouselLayout: false,
        disableSearchFocus: true,
        filterControlsWidth: "70%",
        columnSelectorProps: columnSelectorProps,
      })}
    />
  );
};

const LaunchDetails = ({ user, marketPlace: { marketPlace }, mediaQuery, amz_onboard }) => {
  const [renderChecklist, setRenderChecklist] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [productEditModalDetails, setProductEditModalDetails] = useState();
  const [showProductEditModal, setShowProductEditModal] = useState(false);
  const { t } = useTranslation(["merchandising"]);
  const fmt = useDateFormatter("monthDay");

  const { date } = useDates();
  const location = useLocation();
  const { id } = Route.useParams();
  const urlParams = useMemo(() => new URLSearchParams(location.searchStr), [location.searchStr]);

  const queryClient = useQueryClient();
  const userCanUpdateProducts = useSimpleCan("update", "Product");
  const userCanExportProducts = useSimpleCan("export", "Product");

  const baseQuery = { marketplace: marketPlace };

  const { data: launchData, isLoading: launchDataLoading } = useFetch(
    ["launch", marketPlace, id],
    `/merchandise/product_launch/${id}`,
    {},
    {
      enabled: !!id,
      select: (d) => d.data,
    },
  );

  const { data: launchProductData } = useFetch(
    ["launch_products", marketPlace, id],
    `/merchandise/product_launch_product_data/${id}`,
    { ...baseQuery },
    {
      enabled: !!id,
      select: (d) => d.data,
    },
  );

  let startDate = urlParams.get("start") ?? launchData?.start_date.slice(0, 10);

  if (launchData?.state?.toUpperCase() === LAUNCH_STATES.PREPARING.value && !urlParams.get("start")) {
    startDate = moment().subtract(30, "day").format("YYYY-MM-DD");
  }

  const endDate =
    moment(launchData?.end_date).isBefore(moment()) ?
      launchData?.end_date.slice(0, 10)
    : moment().subtract(1, "day").format("YYYY-MM-DD");

  const productFilter = useMemo(
    () =>
      launchData?.products?.length ? [{ id: "product_id", value: { op: "anyof", value: launchData.products } }] : [],
    [launchData?.products],
  );

  const v4DateParams = useMemo(() => {
    if (!startDate || !endDate) return {};
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffDays = moment(endDate).diff(moment(startDate), "days");
    const preStart = moment(startDate).subtract(diffDays, "days").toDate();
    const preEnd = moment(startDate).subtract(1, "days").toDate();
    return { start, end, preStart, preEnd };
  }, [startDate, endDate]);

  const totalsGetter = useGetV4ProductReportTotals();
  const totalsQuery = useQuery({
    ...totalsGetter({ filters: productFilter, ...v4DateParams }),
    placeholderData: keepPreviousData,
    enabled: !!launchData?.products?.length,
  });

  const byDateGetter = useGetV4ProductReportByDate();
  const byDateQuery = useQuery({
    ...byDateGetter({
      filters: productFilter,
      ...v4DateParams,
      group: urlParams.get("group") ?? undefined,
    }),
    placeholderData: keepPreviousData,
    enabled: !!launchData?.products?.length,
  });

  const byProductGetter = useGetV4ProductReportByProduct();
  const byProductQuery = useQuery({
    ...byProductGetter({
      filters: productFilter,
      pagination: { pageIndex: 0, pageSize: launchData?.products?.length || 10 },
      sorting: [],
      ...v4DateParams,
    }),
    placeholderData: keepPreviousData,
    enabled: !!launchData?.products?.length,
  });

  const isLoading = totalsQuery.isLoading || byDateQuery.isLoading || byProductQuery.isLoading;

  const exportMutation = useV4ProductReportExport(productFilter);

  // http://localhost:8000/api/gvads/advproduct/?plp=77
  const { data: launchProductAds } = useFetch(
    ["launch_product_ads", marketPlace, id],
    `/api/gvads/advproduct/`,
    { plp: id },
    {
      enabled: !!id,
      select: (d) => d.data,
      default: [],
    },
  );

  const { data: launchPromoPlans } = useFetch(
    ["launch_product_promo_plans", marketPlace, id],
    `/merchandise/plans/`,
    { plp: id },
    {
      enabled: !!id,
      select: (d) => d.data?.results,
      default: [],
    },
  );

  const { data: categoriesData } = useFetch(
    ["categories"],
    "/api/gvads/categories/",
    { marketplace: marketPlace, channel: "amazon" },
    {},
  );

  const updateProductState = useMutation({
    mutationFn: async (data) => {
      return api.patch(`/merchandise/gvproducts/${data.id}/`, data);
    },

    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: ["plans"],
      });
    },
  });

  const onUpdateStatus = (updateData) => {
    updateProductState.mutate({
      sales_state: updateData.sales_state,
      id: updateData.id,
    });

    // invalidateQueries below declares all cached queries as stale when left empty, needed for product sales state changes
    queryClient.invalidateQueries();
  };

  const onUpdateAllStatuses = () => {
    launchData.products.forEach((product) => {
      onUpdateStatus({
        id: product,
        sales_state: launchProductData?.find((p) => p.id === product)?.recommended_sales_state,
      });
    });
  };

  const statsToRender = useMemo(() => {
    const statsToInclude = ["bsr", "cost", "ad_sales", "page_views"];
    const { goal_metrics: goals, exit_criteria: exitCriteria } = launchData ?? {};
    if (exitCriteria?.sales) {
      statsToInclude.push("sales");
    }

    if (exitCriteria?.units) {
      statsToInclude.push("units");
    }

    if (goals?.acos) {
      statsToInclude.push("roas");
    }

    if (exitCriteria?.num_of_ratings) {
      statsToInclude.push("ratings_total");
    }

    if (goals?.min_rating) {
      statsToInclude.push("rating");
    }

    if (goals?.margin) {
      statsToInclude.push("margin");
    }

    return { stats: statsToInclude };
  }, [launchData]);

  const preparingState = launchData?.state?.toUpperCase() === LAUNCH_STATES.PREPARING.value;

  // Below memo controls whether or not to render checklist
  // Currently showing right after launch or when details visited without any associated ads or promo plans
  const showChecklist = useMemo(() => {
    return preparingState && (location?.state?.showChecklist || !launchProductAds?.length || !launchPromoPlans?.length);

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [launchProductAds, location?.state?.showChecklist, launchData, id]);

  const [launchMetrics, launchProductColumns, preparingColumns, scorecardColumns] = useMemo(() => {
    if (totalsQuery.data) {
      return [
        [
          buildLaunchMetric(
            { marketPlace: marketPlace },
            null,
            "sales",
            "sales_percent",
            preparingState ?
              {
                override: true,
                overrideValue: launchData?.exit_criteria?.sales,
                overrideLabel: ($) => $.merchandising["Target Sales"],
              }
            : {
                overrideTarget: true,
                overrideTargetLabel: ($) => $.merchandising["vs. Goal"],
                overrideTargetValue: launchData?.exit_criteria?.sales,
                overrideTargetFormatter: (v) =>
                  formatCurrency(v, {
                    marketPlace: marketPlace,
                    compact: true,
                  }),
              },
          ),
          buildLaunchMetric({ marketPlace: marketPlace }, null, "ad_sales", "ad_sales_percent"),
          buildLaunchMetric({ marketPlace: marketPlace }, null, "cost", "cost_percent", {
            overrideTarget: true,
            overrideTargetLabel: ($) => $.merchandising["vs. Goal"],
            overrideTargetValue: launchData?.advertising_budget,
            overrideTargetLower: true,
            overrideTargetFormatter: (v) =>
              formatCurrency(v, {
                marketPlace: marketPlace,
                compact: true,
              }),
          }),
          buildLaunchMetric(
            { marketPlace: marketPlace },
            null,
            "units",
            "units_percent",
            preparingState ?
              {
                override: true,
                overrideValue: launchData?.exit_criteria?.units,
                overrideLabel: ($) => $.merchandising["Target Units"],
              }
            : {
                overrideTarget: true,
                overrideTargetLabel: ($) => $.merchandising["vs. Goal"],
                overrideTargetValue: launchData?.exit_criteria?.units,
                overrideTargetFormatter: (v) => formatNumber(v),
              },
          ),
          buildLaunchMetric({ marketPlace: marketPlace }, null, "rating", "rating_percent", {
            override: preparingState,
            overrideValue: launchData?.goal_metrics?.min_rating,
            overrideLabel: ($) => $.merchandising["Target Rating"],
            overrideTarget: !preparingState,
            overrideTargetLabel: ($) => $.merchandising["vs. Goal"],
            overrideTargetValue: launchData?.goal_metrics?.min_rating,
            overrideTargetFormatter: (v) => formatNumber(v, { decimalPlaces: 1 }),
            metric: {
              decimalPlaces: 1,
              name: "Rating",
              toolTip: "The average rating across all products enrolled in New Product Introduction",
              totalOverrideAccessor: preparingState ? null : "last_rating",
            },
          }),
          buildLaunchMetric({ marketPlace: marketPlace }, null, "ratings_total", "ratings_total_percent", {
            override: preparingState,
            overrideValue: launchData?.exit_criteria?.num_of_ratings,
            overrideLabel: ($) => $.merchandising["Target # of Ratings"],
            overrideTarget: !preparingState,
            overrideTargetLabel: ($) => $.merchandising["vs. Goal"],
            overrideTargetValue: launchData?.exit_criteria?.num_of_ratings,
            overrideTargetFormatter: (v) => formatNumber(v, { decimalPlaces: 0 }),
            metric: {
              name: "# of Ratings",
              toolTip: "The number of ratings for all products enrolled in New Product Introduction",
              totalOverrideAccessor: preparingState ? null : "last_ratings_total",
            },
          }),
          buildLaunchMetric({ marketPlace: marketPlace }, null, "bsr", "bsr_percent", {
            metric: {
              totalOverrideAccessor: "last_bsr_rank",
            },
          }),
          buildLaunchMetric({ marketPlace: marketPlace }, null, "page_views", "page_views", {
            overrideTarget: !preparingState,
            overrideTargetLabel: null,
            overrideTargetValue: null,
          }),
          buildLaunchMetric(
            { marketPlace: marketPlace },
            null,
            "margin",
            "margin_delta",
            preparingState ?
              {
                override: true,
                overrideValue: launchData?.goal_metrics?.margin / 100,
                overrideLabel: ($) => $.merchandising["Target Margin"],
              }
            : {
                overrideTarget: true,
                overrideTargetLabel: ($) => $.merchandising["vs. Goal"],
                overrideTargetValue: launchData?.goal_metrics?.margin / 100,
              },
          ),
          buildLaunchMetric(
            { marketPlace: marketPlace },
            null,
            "roas",
            "roas_delta",
            preparingState ?
              {
                override: true,
                overrideValue: 1 / launchData?.goal_metrics?.acos,
                overrideLabel: ($) => $.merchandising["Target ROAS"],
              }
            : {
                overrideTarget: true,
                overrideTargetLabel: ($) => $.merchandising["vs. Goal"],
                overrideTargetValue: 1 / (launchData?.goal_metrics?.acos / 100),
                overrideTargetFormatter: (v) => formatNumber(v, { decimalPlaces: 1 }),
              },
          ),
        ]
          .filter((m) =>
            preparingState ? ["sales", "units", "ratings_total", "rating", "margin", "roas"]?.includes(m.id) : m,
          )
          ?.filter((m) => {
            return statsToRender?.stats?.includes(m.id);
          }),
        [
          col("", "expander", "isExpanded", {
            width: 60,
            hideFilter: true,
            isStatic: true,
          }),
          col(
            t(($) => $.merchandising["Product"]),
            "product",
            "product_title",
            {
              className: "text-left font-weight-bold",
              width: 300,
              mobileWidth: 200,
              showDot: false,
              fixedLeft: true,
              hideFilter: true,
              isStatic: true,
            },
          ),

          col(
            t(($) => $.merchandising["COGS"]),
            "cogs",
            "cogs",
            {
              cellFunctions: {
                productEditModalDetails,
                setProductEditModalDetails,
                showProductEditModal,
                setShowProductEditModal,
              },
              cellClasses: 'd-flex justify-content-end align-items-center"',
            },
          ),
          col(
            t(($) => $.merchandising["Price"]),
            "currency",
            "product_price",
          ),
          col(
            t(($) => $.merchandising["Stock"]),
            "stock",
            "stock_status",
          ),
          col(
            t(($) => $.merchandising["Total Sales"]),
            "currency",
            "total_sales",
          ),
          col(
            t(($) => $.merchandising["Ad Sales"]),
            "currency",
            "ad_sales",
          ),
          col(
            t(($) => $.merchandising["Ad Spend"]),
            "currency",
            "cost",
          ),
          col(
            t(($) => $.merchandising["Units"]),
            "number",
            "total_units",
          ),
          col(
            t(($) => $.merchandising["Profit"]),
            "currency",
            "profit",
          ),
          col(
            t(($) => $.merchandising["Margin"]),
            "percent",
            "margin",
          ),
          col(
            t(($) => $.merchandising["# of Ratings"]),
            "number",
            "ratings_total",
          ),
          col(
            t(($) => $.merchandising["Rating"]),
            "number",
            "rating",
          ),
          col(
            t(($) => $.merchandising["Page Views"]),
            "number",
            "page_views",
          ),
          col(
            t(($) => $.merchandising["Impressions"]),
            "number",
            "impressions",
          ),
          col(
            t(($) => $.merchandising["Clicks"]),
            "number",
            "clicks",
          ),
          col(
            t(($) => $.merchandising["BSR"]),
            "number",
            "bsr_rank",
          ),
          col(
            t(($) => $.merchandising["ROAS"]),
            "number",
            "roas",
          ),
        ],
        [
          col("", "expander", "isExpanded", {
            width: 40,
            hideFilter: true,
            isStatic: true,
          }),
          col(
            t(($) => $.merchandising["Product"]),
            "product",
            "product_title",
            {
              className: "text-left font-weight-bold",
              width: 750,
              mobileWidth: 200,
              showDot: false,
              fixedLeft: true,
              hideFilter: true,
              isStatic: true,
            },
          ),
          col(
            t(($) => $.merchandising["COGS"]),
            "cogs",
            "cogs",
            {
              cellFunctions: {
                productEditModalDetails,
                setProductEditModalDetails,
                showProductEditModal,
                setShowProductEditModal,
              },
              width: 150,
              cellClasses: 'd-flex justify-content-end align-items-center"',
            },
          ),
          col(
            t(($) => $.merchandising["Price"]),
            "currency",
            "product_price",
            { width: 150 },
          ),
          col(
            t(($) => $.merchandising["Stock"]),
            "stock",
            "stock_status",
            { width: 150 },
          ),
        ],
        [
          col("", "expander", "isExpanded", {
            width: 60,
            hideFilter: true,
            isStatic: true,
          }),
          col(
            t(($) => $.merchandising["Product"]),
            "product",
            "product_title",
            {
              className: "text-left font-weight-bold",
              width: 300,
              mobileWidth: 200,
              showDot: false,
              fixedLeft: true,
              hideFilter: true,
              isStatic: true,
            },
          ),
          // col("Product", "text", "sku"),
          col(
            t(($) => $.merchandising["Total Sales"]),
            "currency",
            "total_sales",
            {
              compareValue: launchData.exit_criteria?.sales,
            },
          ),
          col(
            t(($) => $.merchandising["Units"]),
            "number",
            "total_units",
            {
              compareValue: launchData.exit_criteria?.units,
            },
          ),
          col(
            t(($) => $.merchandising["# of Ratings"]),
            "number",
            "ratings_total",
            {
              compareValue: launchData?.exit_criteria?.num_of_ratings,
            },
          ),
          col(
            t(($) => $.merchandising["Rating"]),
            "number",
            "rating",
            {
              compareValue: launchData?.goal_metrics?.min_rating,
              staticCompareValue: true,
            },
          ),
          col(
            t(($) => $.merchandising["ROAS"]),
            "number",
            "roas",
            {
              compareValue: 1 / (parseFloat(launchData?.goal_metrics?.acos) / 100),
              staticCompareValue: true,
            },
          ),
          col(
            t(($) => $.merchandising["Margin"]),
            "percent",
            "margin",
            {
              compareValue: launchData.goal_metrics?.margin / 100,
              staticCompareValue: true,
            },
          ),
        ],
      ];
    }
    return [[], [], [], []];

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [totalsQuery.data]);

  return (
    <DatesProvider
      defaults={{
        period: "custom",
        start: startDate,
        end: launchData?.end_date.slice(0, 10),
      }}
    >
      <BreadcrumbDates
        items={[
          {
            name: t(($) => $.merchandising["New Product Launch"]),
            href: `/user/productlaunch?start=${moment().subtract(30, "days")?.format("YYYY-MM-DD")}`,
          },
          {
            name: launchData?.name,
            href: `/user/productlaunch/launches/${launchData?.id}`,
          },
        ]}
        groupByOnly={!preparingState}
        hideRange={preparingState}
        hideCompare={true}
      />
      {isLoading || !launchData?.state ?
        <Loading />
      : <Row className="px-5">
          <MetricsCard
            id={"productLaunchDetails"}
            mobileStyle
            title={launchData?.name}
            byline={
              <>
                {t(($) => $.merchandising["{startDate} to {endDate}"], {
                  startDate: fmt.format(moment(launchData?.start_date).toDate()),
                  endDate: fmt.format(moment(launchData?.end_date).toDate()),
                })}
              </>
            }
            selectedCategory={launchData.category}
            totals={{
              data: totalsQuery.data?.data,
              loading: isLoading || launchDataLoading,
            }}
            timeline={{
              data: byDateQuery.data?.data || [],
              loading: isLoading || launchDataLoading,
            }}
            inlineStatus={
              <div
                style={{
                  display: "flex",
                  justifyContent: mediaQuery ? "center" : "space-between",
                  textAlign: mediaQuery ? "center" : "inherit",
                  marginTop: mediaQuery ? "30px" : "inherit",
                }}
              >
                {launchData?.state?.toUpperCase() === LAUNCH_STATES.INTRODUCED.value && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "2.6rem",
                        color: "#403E3D",
                        fontWeight: 600,
                      }}
                    >
                      <span>
                        {(
                          (moment().diff(moment(launchData?.start_date), "days") /
                            moment(launchData?.end_date).diff(moment(launchData?.start_date), "days")) *
                          100
                        ).toFixed(0) ?? 0}
                      </span>
                      %
                    </span>
                    <span
                      style={{
                        fontSize: "1rem",
                        color: "#403E3D",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        marginTop: "-.7rem",
                      }}
                    >
                      {t(($) => $.merchandising["Complete"])}
                    </span>
                  </div>
                )}
                <PlanStatus
                  status={launchData?.state?.toLowerCase()}
                  showLabel
                  due={launchData?.start_date}
                  end={launchData?.end_date}
                  start={launchData?.start_date}
                />
              </div>
            }
            showMetricsSelector={true}
            defaultTargetColor={true}
            context={"product"}
            initialSelectedMetric={
              preparingState ?
                ["total_sales", "units", "ratings_total", "rating", "margin", "roas"]
              : [
                  "total_sales",
                  "ad_sales",
                  "cost",
                  "units",
                  "number_of_ratings",
                  "rating",
                  "page_views",
                  "bsr",
                  "margin",
                  "acos",
                ]
            }
            metrics={launchMetrics}
            group={urlParams.get("group") ?? "day"}
          >
            <Row style={{ paddingBottom: "150px" }}>
              <Row>
                <Col
                  xs={12}
                  style={{
                    display: "flex",
                    justifyContent: mediaQuery ? "center" : "flex-end",
                  }}
                  className="mt-3"
                >
                  {launchData?.state?.toUpperCase() === LAUNCH_STATES.LAUNCHED.value &&
                    launchProductData?.some((p) => p.sales_state === "NEW_PRODUCT") &&
                    userCanUpdateProducts && (
                      <ConfirmationModal
                        title={t(($) => $.merchandising["Confirm Launch Completion"])}
                        onConfirm={onUpdateAllStatuses}
                        modalButtonText={t(($) => $.merchandising["Close Launch"])}
                        confirmButtonText={t(($) => $.merchandising["Close Launch"])}
                        confirmationMessage={
                          <>
                            <p>{t(($) => $.merchandising["Are you sure you want to mark this launch complete?"])}</p>
                            <p>
                              {t(
                                ($) =>
                                  $.merchandising[
                                    "After confirming, all products will be automatically classified based on Trellis’ recommendations if they have not yet been reviewed."
                                  ],
                              )}
                            </p>
                          </>
                        }
                      />
                    )}
                  {userCanUpdateProducts && <LaunchEditModal launchData={launchData} id={id} />}
                </Col>
              </Row>
              {preparingState && (
                <Row>
                  <Accordion
                    title={
                      <>
                        {t(($) => $.merchandising["Next Steps"])}
                        <HoverInfo iconCss={{ color: "grey", paddingStart: "xsmall" }}>
                          {t(
                            ($) =>
                              $.merchandising["View recommended steps to make your launch as successful as possible"],
                          )}
                        </HoverInfo>
                      </>
                    }
                    defaultExpanded={true}
                  >
                    <Row>
                      <Col xs={12}>
                        <LaunchPlanningDialogue
                          launchData={launchData ?? {}}
                          launchProductAds={launchProductAds}
                          launchProductPromos={launchPromoPlans}
                          categories={categoriesData?.data ?? []}
                          marketPlace={marketPlace}
                          renderChecklist={renderChecklist}
                          setRenderChecklist={setRenderChecklist}
                          showChecklist={showChecklist}
                          user={user}
                          amz_onboard={amz_onboard}
                        />
                      </Col>
                    </Row>
                  </Accordion>
                </Row>
              )}
              {/* Launch Product Scorecard */}
              {!preparingState && (
                <Accordion
                  title={
                    <>
                      {t(($) => $.merchandising["Scorecard"])}
                      <HoverInfo iconCss={{ color: "grey", paddingStart: "xsmall" }}>
                        {t(
                          ($) =>
                            $.merchandising["Compare individual product performance against goals and exit criteria"],
                        )}
                      </HoverInfo>
                    </>
                  }
                  defaultExpanded={true}
                  height={"100%"}
                >
                  <Row>
                    {launchMetrics
                      ?.filter((m) => ["sales", "units", "rating", "ratings_total", "margin", "roas"]?.includes(m.id))
                      ?.map((m, i) => {
                        return (
                          <Col xs={12} md={6} lg={2} key={m.id} className={`mb-4 ${i % 2 === 0 ? "ps-2" : "pe-2"}`}>
                            <DetailedMetric
                              center
                              metric={m}
                              data={totalsQuery.data?.data}
                              showTarget={true}
                              targetLabel={t(($) => $.merchandising["vs. Goal"])}
                            />
                          </Col>
                        );
                      })}

                    <Col xs={12} className="mb-5">
                      <Table
                        stats={[
                          ...(byProductQuery.data?.data?.map((p) => ({
                            ...p,
                            id: p.product_id,
                          })) ?? []),
                        ]}
                        launchData={launchData ?? []}
                        selectedProduct={selectedProduct}
                        setSelectedProduct={setSelectedProduct}
                        expandedRow={expandedRow}
                        setExpandedRow={setExpandedRow}
                        preparingState={preparingState}
                        marketPlace={marketPlace}
                        columnSpecs={scorecardColumns}
                        date={date}
                        user={user}
                        isLoading={isLoading}
                        onUpdateStatus={onUpdateStatus}
                        isReadOnly={!userCanUpdateProducts}
                      />
                    </Col>
                  </Row>
                </Accordion>
              )}
              {/* LAUNCH PRODUCT DETAILS TABLE */}
              <Accordion
                title={
                  <>
                    {t(($) => $.merchandising["Products In Launch"])}{" "}
                    <HoverInfo iconCss={{ color: "grey", paddingStart: "xsmall" }}>
                      {t(
                        ($) =>
                          $.merchandising["Select a product to show individual product performance in the chart above"],
                      )}
                    </HoverInfo>
                  </>
                }
                defaultExpanded={preparingState}
                height={"100%"}
              >
                <Row>
                  <Col xs={12} className="mb-5">
                    {showProductEditModal && (
                      <CogsEditModal
                        showProductEditModal={showProductEditModal}
                        setShowProductEditModal={setShowProductEditModal}
                        productEditModalDetails={productEditModalDetails}
                        setProductEditModalDetails={setProductEditModalDetails}
                        marketPlace={marketPlace}
                      />
                    )}
                    <Table
                      stats={
                        byProductQuery.data?.data?.map((p) => ({
                          ...p,
                          id: p.product_id,
                        })) ?? []
                      }
                      launchData={launchData ?? []}
                      selectedProduct={selectedProduct}
                      setSelectedProduct={setSelectedProduct}
                      expandedRow={expandedRow}
                      setExpandedRow={setExpandedRow}
                      preparingState={preparingState}
                      marketPlace={marketPlace}
                      columnSpecs={preparingState ? preparingColumns : launchProductColumns}
                      date={date}
                      user={user}
                      isLoading={isLoading}
                      onUpdateStatus={onUpdateStatus}
                      isReadOnly={!userCanUpdateProducts}
                    />
                  </Col>
                </Row>
              </Accordion>
              {!isLoading && userCanExportProducts && (
                <Col xs={12} className="text-right py-3">
                  <DownloadV4Report downloadMutation={exportMutation} />
                </Col>
              )}
            </Row>
          </MetricsCard>
        </Row>
      }
    </DatesProvider>
  );
};

const mapStateToProps = (state) => ({
  marketPlace: state.marketPlace,
  user: state.user,
  channel: state.channel,
  amz_onboard: state.amz_onboard,
});

export const Route = createFileRoute("/_authenticated/user/productlaunch/launches/$id")({
  component: withMediaQuery("(max-width:600px)")(connect(mapStateToProps)(LaunchDetails)),
});
